import { useState } from 'react';

import {
  Box,
  Card as MuiCard,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import { alpha } from '@mui/material/styles';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import Screen from '@/karehero-app/explore/Screen';
import Chat, { TalkInput } from '@/karehero-app/explore/Chat';
import { AA_MAPPING } from '@/karehero-app/explore/tell-us-once/TellUsOnce.content';
import {
  AA_TOTALS,
  aaQuestions,
} from '@/karehero-app/explore/tell-us-once/aaQuestions';

// One moment in the conversation, and everywhere it lands on the form.
//
// Read conversation-first on purpose. A form is linear because paper is
// linear; a conversation is not. One thing someone says fills fields on
// several pages at once, and one page is usually filled from moments minutes
// apart. Pairing each page with "its" conversation — the first version of this
// — asserted a tidiness that does not exist, and hid the very thing that makes
// talking better than filling it in: the scatter.
//
// The left half is a real phone running the real conversation components. The
// right half is the genuine PDF, so both sides of the comparison are true
// things rather than approximations.

const PHONE = { width: 375, height: 812, scale: 0.75 };

// A4-ish, big enough to actually read the form's questions. Sized to A4's
// ratio on purpose: the highlight regions are percentages of this box, so a
// matching aspect keeps them close to where they sit on the rendered page.
const PAGE = { width: 440, height: 622 };

// A phone at a fixed scale, sized to sit level with the landings beside it.
// Deliberately not ExploreScreen: this is a panel element rather than a frame
// in its own right, so it carries no label and no copy affordance.
const Phone = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      width: PHONE.width * PHONE.scale,
      height: PHONE.height * PHONE.scale,
      flexShrink: 0,
      border: 1,
      borderColor: 'custom.neutral.200',
      borderRadius: `${radius.lg}px`,
      overflow: 'hidden',
      bgcolor: 'background.default',
    }}
  >
    <Box
      sx={{
        width: PHONE.width,
        height: PHONE.height,
        overflow: 'hidden',
        transform: `scale(${PHONE.scale})`,
        transformOrigin: 'top left',
      }}
    >
      {children}
    </Box>
  </Box>
);

const ColumnLabel = ({ label }: { label: string }) => (
  <Typography variant='overline' color='text.secondary'>
    {label}
  </Typography>
);

// Currently unrendered — see the commented block at the foot of the carousel.
// The section's real questions, from KareHero's normalised schema, with the
// Required column that the PDF itself cannot answer — the government form
// flags nothing as required, so this is the only place that knowledge lives.
const QuestionTable = ({
  section,
  page,
  title,
}: {
  section: string;
  page: number;
  title: string;
}) => {
  const questions = aaQuestions(section);
  if (!questions.length) return null;
  const required = questions.filter((q) => q.required).length;

  return (
    <Stack spacing={1}>
      <Stack sx={{ alignItems: 'baseline' }} direction='row' spacing={1.5}>
        <Typography variant='subtitle2'>
          Page {page} · {title}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {required} of {questions.length} required
        </Typography>
      </Stack>

      <MuiCard sx={{ p: 0, overflow: 'hidden' }}>
        <Table size='small'>
          <TableBody>
            {questions.map((q) => (
              <TableRow key={q.id}>
                <TableCell width={92} sx={{ verticalAlign: 'top' }}>
                  <Chip
                    size='small'
                    label={q.required ? 'Required' : 'Optional'}
                    color={q.required ? 'warmOrange' : undefined}
                  />
                </TableCell>
                {/* Follow-ups sit indented under the question that opens
                    them, tagged with the answer that does it. The form's
                    branching is the thing A3 argues about, so it has to be
                    visible rather than summarised as a count. */}
                <TableCell sx={{ verticalAlign: 'top', pl: q.depth * 2 }}>
                  <Typography variant='body2'>{q.label}</Typography>
                  {q.revealedBy && (
                    <Typography
                      sx={{ display: 'block' }}
                      variant='caption'
                      color='custom.coral.700'
                    >
                      {q.revealedBy}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MuiCard>
    </Stack>
  );
};

// One page of the real form, large enough to read, with the part this moment
// answers ringed on it. Inert: a live PDF viewer in the middle of the page
// would swallow every wheel event crossing it.
//
// The region is given in percentages of the page box, and the box is sized to
// A4 so those percentages land close to the rendered page. Close, not exact —
// they are hand-placed guesses, same as the page numbers.
const FormPage = ({
  page,
  section,
  region,
}: {
  page: number;
  section: string;
  region?: { top: number; left: number; width: number; height: number };
}) => (
  <Stack sx={{ width: PAGE.width, flexShrink: 0 }} spacing={1}>
    <Box
      sx={{
        position: 'relative',
        height: PAGE.height,
        border: 1,
        borderColor: 'custom.neutral.200',
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: 'custom.neutral.100',
      }}
    >
      <Box
        component='iframe'
        src={`${AA_MAPPING.pdf}#page=${page}&toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
        title={`Attendance Allowance form, page ${page}`}
        sx={{
          width: '100%',
          height: '100%',
          border: 0,
          display: 'block',
          pointerEvents: 'none',
        }}
      />

      {region && (
        <Box
          sx={{
            position: 'absolute',
            top: `${region.top}%`,
            left: `${region.left}%`,
            width: `${region.width}%`,
            height: `${region.height}%`,
            border: 2,
            borderColor: 'custom.coral.600',
            borderRadius: '6px',
            pointerEvents: 'none',
            // A wash rather than a block: the form underneath still has to be
            // readable, since reading it is the reason it is here.
            bgcolor: (t) => alpha(t.palette.custom.coral[600], 0.14),
          }}
        />
      )}
    </Box>
    <Typography variant='subtitle2'>
      Page {page} · {section}
    </Typography>
  </Stack>
);

const AaFormCarousel = () => {
  const [index, setIndex] = useState(0);
  const moments = AA_MAPPING.moments;
  const moment = moments[index];

  // The frame sits inside a click-to-copy target, so the controls stop
  // propagation — otherwise paging would copy a direction instead. Wraps at
  // both ends so it never dead-ends on a disabled arrow.
  const go = (delta: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setIndex((i) => (i + delta + moments.length) % moments.length);
  };

  const fieldCount = moment.lands.reduce((n, l) => n + l.fields.length, 0);

  return (
    <Stack spacing={2}>
      <Stack
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        direction='row'
        spacing={2}
      >
        <Stack sx={{ alignItems: 'center' }} direction='row' spacing={0.5}>
          <IconButton
            size='small'
            onClick={go(-1)}
            aria-label='Previous moment'
          >
            <Icon icon={'chevron-left' as never} fontSize={14} />
          </IconButton>
          <Typography variant='body2' color='text.secondary'>
            Moment {index + 1} of {moments.length} · {moment.at}
          </Typography>
          <IconButton size='small' onClick={go(1)} aria-label='Next moment'>
            <Icon icon={'chevron-right' as never} fontSize={14} />
          </IconButton>
        </Stack>

        {/* The headline number: how far one moment reaches. Landing on three
            pages at once is the argument for talking over filling in. */}
        <Chip
          size='small'
          label={`${moment.lands.length} ${
            moment.lands.length === 1 ? 'page' : 'pages'
          } · ${fieldCount} fields`}
          color={moment.lands.length > 1 ? 'warmOrange' : undefined}
        />
      </Stack>

      <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={2}>
        <Stack sx={{ flexShrink: 0 }} spacing={1}>
          <ColumnLabel label='WHAT THEY SAY' />
          <Phone>
            <Screen
              footer={
                <TalkInput typePrompt='Or type instead' state='listening' />
              }
            >
              <Stack sx={{ pt: 1 }} spacing={2}>
                <Chat turns={moment.turns} />

                {/* Named document types, so "something official" isn't a dead
                    end — and an upload route, because one document answers
                    several fields at once. */}
                {moment.examples && (
                  <Stack
                    sx={{ flexWrap: 'wrap' }}
                    direction='row'
                    spacing={0.75}
                    useFlexGap
                  >
                    {moment.examples.map((e) => (
                      <Chip key={e} size='small' label={e} />
                    ))}
                  </Stack>
                )}

                {moment.options && (
                  <Stack spacing={1}>
                    {moment.options.map((o) => (
                      <Paper key={o.label} variant='outlined'>
                        <Stack
                          sx={{ alignItems: 'center', p: 1.5 }}
                          direction='row'
                          spacing={1.5}
                        >
                          <Box
                            sx={{ color: 'custom.coral.600', display: 'flex' }}
                          >
                            <Icon icon={o.icon as never} fontSize={16} />
                          </Box>
                          <Typography
                            sx={{ flex: 1, minWidth: 0 }}
                            variant='body2'
                          >
                            {o.label}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Screen>
          </Phone>
        </Stack>

        <Stack
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            flexShrink: 0,
            height: PHONE.height * PHONE.scale,
          }}
        >
          <Box sx={{ color: 'custom.coral.600', display: 'flex' }}>
            <Icon icon={'long-arrow-right' as never} fontSize={22} />
          </Box>
        </Stack>

        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1.5}>
          <ColumnLabel
            label={
              moment.lands.length === 1
                ? 'THE PAGE IT TOUCHES'
                : 'THE PAGES IT TOUCHES'
            }
          />
          <Box sx={{ pb: 1, overflowX: 'auto' }}>
            <Stack
              sx={{ alignItems: 'flex-start' }}
              direction='row'
              spacing={3}
            >
              {moment.lands.map((l) => (
                <FormPage
                  key={`${l.page}-${l.section}`}
                  page={l.page}
                  section={l.section}
                  region={l.region}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Stack>

      {/* Hidden for now — the question tables made the section very long, and
          the pages plus the conversation carry the argument on their own. The
          block is intact rather than deleted: the required/optional split is
          the one thing the PDF cannot tell you, so it will likely come back.

      <Stack spacing={3} pt={1}>
        <ColumnLabel label='WHAT THOSE PAGES ASK' />
        {moment.lands.map((l) =>
          l.schema ? (
            <QuestionTable
              key={`${l.page}-${l.section}`}
              section={l.schema}
              page={l.page}
              title={l.section}
            />
          ) : null,
        )}
      </Stack>

      */}

      <Typography variant='caption' color='text.secondary'>
        Across the whole form: {AA_TOTALS.required} of {AA_TOTALS.questions}{' '}
        questions are required. The government PDF flags none of them — that
        comes from KareHero&rsquo;s own schema. {AA_MAPPING.unverifiedNote}
      </Typography>
    </Stack>
  );
};

AaFormCarousel.displayName = 'AaFormCarousel';
export default AaFormCarousel;
